import { useState } from "react";
import { ArrowRight, Sparkles, MessageCircle, FileText } from "lucide-react";
import { Link } from "@tanstack/react-router";

type QA = {
  question: string;
  section: string;
  answer: string;
};

const qaBank: Record<string, QA> = {
  "What happens if I break my lease early?": {
    question: "What happens if I break my lease early?",
    section: "Section 4.2 · Early Termination",
    answer:
      "If you move out before the lease ends, you owe up to 2 months' rent as a fee, plus rent until the unit is re-rented. The good news: California law requires your landlord to actively try to re-rent the unit (mitigate damages) — they can't just charge you for the full remaining term.",
  },
  "What should I negotiate?": {
    question: "What should I negotiate?",
    section: "Section 7.1 · Repairs & Deposits",
    answer:
      "Push back on the $500 'administrative move-out fee' — it's not standard. Also negotiate a 30-day cure period for late rent (the lease currently allows eviction after 3 days) and cap the security deposit interest at the legal rate.",
  },
  "Is this legal in California?": {
    question: "Is this legal in California?",
    section: "Section 12.4 · Waivers",
    answer:
      "Mostly yes, but two clauses are unenforceable in California: (1) the waiver of your right to a jury trial, and (2) the clause requiring you to pay the landlord's attorney fees regardless of outcome. California Civil Code §1717 makes attorney-fee clauses mutual.",
  },
};

const suggested = ["What should I negotiate?", "Is this legal in California?"];

export function AskAIDemo() {
  const [active, setActive] = useState<string>("What happens if I break my lease early?");
  const [loading, setLoading] = useState(false);

  const handlePick = (q: string) => {
    if (q === active || loading) return;
    setLoading(true);
    setTimeout(() => {
      setActive(q);
      setLoading(false);
    }, 1200);
  };

  const current = qaBank[active];

  return (
    <section className="bg-surface/40 py-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-primary-glow" />
            Ask AI · Live demo
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Ask any question about your contract
          </h2>
          <p className="mt-3 text-muted-foreground">
            Try it on a sample lease — no signup, no upload.
          </p>
        </div>

        <div className="rounded-3xl border border-border bg-card p-5 shadow-card sm:p-7">
          {/* User bubble */}
          <div className="flex items-start justify-end gap-3">
            <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary px-4 py-3 text-sm font-medium text-primary-foreground shadow-sm sm:text-base">
              {current.question}
            </div>
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground"
              aria-hidden
            >
              You
            </div>
          </div>

          {/* AI response */}
          <div className="mt-5 flex items-start gap-3">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-glow shadow-elegant"
              aria-hidden
            >
              <MessageCircle className="h-4 w-4 text-primary-foreground" strokeWidth={2.5} />
            </div>

            <div className="min-w-0 flex-1">
              {loading ? (
                <div className="inline-flex items-center gap-1.5 rounded-2xl rounded-tl-sm bg-muted px-4 py-4">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/70 [animation-delay:-0.3s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/70 [animation-delay:-0.15s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/70" />
                </div>
              ) : (
                <div className="animate-fade-in rounded-2xl rounded-tl-sm border border-border bg-background p-4 shadow-sm sm:p-5">
                  <div className="mb-3 inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary-glow">
                    <FileText className="h-3 w-3" strokeWidth={2.5} />
                    {current.section}
                  </div>
                  <p className="text-sm leading-relaxed text-foreground sm:text-base">
                    {current.answer}
                  </p>
                  <Link
                    to="/"
                    hash="analyzer"
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-glow hover:underline"
                  >
                    Try it on your own document
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <p className="mt-3 text-[10px] uppercase tracking-wider text-muted-foreground/70">
                    Not legal advice · For informational purposes only
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Suggested chips */}
          <div className="mt-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Try another question
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {suggested.map((q) => {
                const isActive = q === active;
                return (
                  <button
                    key={q}
                    type="button"
                    onClick={() => handlePick(q)}
                    disabled={loading || isActive}
                    className={
                      "w-full rounded-full border px-4 py-2 text-left text-sm font-medium transition-all sm:w-auto " +
                      (isActive
                        ? "border-primary bg-primary/10 text-primary-glow"
                        : "border-border bg-background text-foreground hover:-translate-y-0.5 hover:border-primary/60 hover:text-primary-glow disabled:opacity-50")
                    }
                  >
                    {q}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
